// Dates and times as text, in the reader's own notation and time zone.
//
// One set of styles for the whole app, so a date reads the same on a ticket,
// in a Google Chat message and in an email. The web app formats for the
// viewer (their browser's conventions, their device's time zone — see
// apps/web/src/lib/dateFormat.ts); Cloud Functions format server-written
// messages for the recipient, from the locale and time zone the recipient's
// app last recorded (pickRecipientTextFormat).
//
// The app is English-only, so a date is ALWAYS written in English words, with
// the reader's REGIONAL conventions — clock, date order, first day of the week
// — rather than in their browser's language: "12 Mar 2026, 15:05" for someone
// whose browser is Dutch, "Mar 12, 2026, 3:05 PM" in the US, "Mar 12, 2026,
// 3:05 PM" with Sunday-first weeks for a Chinese-language browser in Taiwan
// (englishRegionalLocale).
//
// Pure: no Firebase imports, safe on both sides.

/** Where and how a date is written: a BCP 47 locale and an IANA time zone. */
export interface DateTextOptions {
  /** `undefined` means the runtime's default locale. */
  locale?: string;
  /** `undefined` means the runtime's local time zone. */
  timeZone?: string;
}

/**
 * The styles the app writes dates in. Examples are en-GB / en-US.
 *   date             12 Mar 2026           Mar 12, 2026
 *   dateLong         12 March 2026         March 12, 2026
 *   dateTime         12 Mar 2026, 15:05    Mar 12, 2026, 3:05 PM
 *   dateTimeSeconds  12 Mar 2026, 15:05:09 Mar 12, 2026, 3:05:09 PM
 *   time             15:05                 3:05 PM
 *   weekdayDate      Thu 12 Mar            Thu, Mar 12
 *   weekdayDateTime  Thu 12 Mar, 15:05     Thu, Mar 12, 3:05 PM
 *   dayMonth         12 Mar                Mar 12
 *   dayMonthTime     12 Mar, 15:05         Mar 12, 3:05 PM
 *   monthYear        Mar 2026              Mar 2026
 *   monthYearLong    March 2026            March 2026
 *   weekdayDayMonthLong  Thursday 12 Mar   Thursday, Mar 12
 */
export type DateTextStyle =
  | 'date'
  | 'dateLong'
  | 'dateTime'
  | 'dateTimeSeconds'
  | 'time'
  | 'weekdayDate'
  | 'weekdayDateTime'
  | 'dayMonth'
  | 'dayMonthTime'
  | 'monthYear'
  | 'monthYearLong'
  | 'weekdayDayMonthLong';

/** The date part of each style; the clock part is added per locale. */
const DATE_PARTS: Record<DateTextStyle, Intl.DateTimeFormatOptions> = {
  date: { day: 'numeric', month: 'short', year: 'numeric' },
  dateLong: { day: 'numeric', month: 'long', year: 'numeric' },
  dateTime: { day: 'numeric', month: 'short', year: 'numeric' },
  dateTimeSeconds: { day: 'numeric', month: 'short', year: 'numeric' },
  time: {},
  weekdayDate: { weekday: 'short', day: 'numeric', month: 'short' },
  weekdayDateTime: { weekday: 'short', day: 'numeric', month: 'short' },
  dayMonth: { day: 'numeric', month: 'short' },
  dayMonthTime: { day: 'numeric', month: 'short' },
  monthYear: { month: 'short', year: 'numeric' },
  monthYearLong: { month: 'long', year: 'numeric' },
  weekdayDayMonthLong: { weekday: 'long', day: 'numeric', month: 'short' },
};

/** Styles that include a clock time, and whether they show seconds. */
const CLOCK: Partial<Record<DateTextStyle, 'minutes' | 'seconds'>> = {
  dateTime: 'minutes',
  dateTimeSeconds: 'seconds',
  time: 'minutes',
  weekdayDateTime: 'minutes',
  dayMonthTime: 'minutes',
};

/**
 * Building an Intl.DateTimeFormat is the expensive part of writing a date —
 * some 25 times the cost of formatting with one already built — and a list
 * such as the access log writes hundreds of dates per render, which a phone
 * feels. So formatters (and the clock each locale uses) are built once and
 * reused. The keys are few: a dozen styles × the handful of locales and
 * zones in use. The cap is only a backstop against an unbounded set.
 */
const MAX_CACHED = 256;
const formatters = new Map<string, Intl.DateTimeFormat>();
const clocks = new Map<string, boolean>();

/** Store `value` under `key`, emptying the cache first if it is full. */
function remember<T>(cache: Map<string, T>, key: string, value: T): T {
  if (cache.size >= MAX_CACHED) cache.clear();
  cache.set(key, value);
  return value;
}

/**
 * Whether a locale writes the clock as 24-hour. Asked of Intl rather than
 * guessed from a list: en-GB and nl-NL resolve to 'h23', en-US to 'h12'.
 */
export function uses24HourClock(locale?: string): boolean {
  // An unnamed locale is the runtime's default, which is fixed for the life
  // of the process (a browser only changes it on restart), so it caches too.
  const key = locale ?? '';
  const known = clocks.get(key);
  if (known !== undefined) return known;
  const hourCycle = new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions().hourCycle;
  return remember(clocks, key, hourCycle === 'h23' || hourCycle === 'h24');
}

/**
 * The clock options for a locale. A 24-hour clock is zero-padded ("07:05"),
 * the way it is read; a 12-hour one is not ("7:05 AM").
 */
function clockOptions(locale: string | undefined, seconds: boolean): Intl.DateTimeFormatOptions {
  const is24 = uses24HourClock(locale);
  return {
    hour: is24 ? '2-digit' : 'numeric',
    minute: '2-digit',
    ...(seconds ? { second: '2-digit' } : {}),
    ...(is24 ? { hourCycle: 'h23' } : {}),
  };
}

/**
 * The formatter for a style, locale and zone. Reused only when the zone is
 * named: a formatter built for "the runtime's zone" keeps the zone it was
 * built in, and a browser's zone changes when its device travels. The web app
 * names the device's zone (apps/web/src/lib/dateFormat.ts) and the server the
 * recipient's, so in practice every formatter is reused.
 */
function formatterFor(style: DateTextStyle, options: DateTextOptions): Intl.DateTimeFormat {
  const { locale, timeZone } = options;
  const key = timeZone === undefined ? undefined : `${style}|${locale ?? ''}|${timeZone}`;
  const known = key === undefined ? undefined : formatters.get(key);
  if (known) return known;
  const clock = CLOCK[style];
  const formatter = new Intl.DateTimeFormat(locale, {
    ...DATE_PARTS[style],
    ...(clock ? clockOptions(locale, clock === 'seconds') : {}),
    timeZone,
  });
  return key === undefined ? formatter : remember(formatters, key, formatter);
}

/** A date or time as text in one of the app's styles. */
export function formatDateText(
  date: Date,
  style: DateTextStyle,
  options: DateTextOptions = {},
): string {
  return formatterFor(style, options).format(date);
}

/** Matches the calendar date at the start of an ISO 8601 string. */
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * A calendar date that is not an instant — a flight's departure date, an
 * all-day booking, a hire date — stored as "YYYY-MM-DD" (or an ISO string
 * whose date part is the one meant). Written as that same date for every
 * reader, whatever their time zone; only the notation changes. Returns the
 * input unchanged if it has no date to read.
 */
export function formatIsoDate(
  isoDate: string,
  style: 'date' | 'dateLong' | 'weekdayDate' | 'dayMonth' | 'monthYear' | 'monthYearLong' | 'weekdayDayMonthLong',
  options: Pick<DateTextOptions, 'locale'> = {},
): string {
  const m = ISO_DATE.exec(isoDate);
  if (!m) return isoDate;
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  // Formatted in UTC because it was built in UTC: the date cannot shift.
  return formatDateText(date, style, { locale: options.locale, timeZone: 'UTC' });
}

/**
 * A time of day that is a setting rather than an instant — quiet hours, a
 * building's opening hours — so no time zone applies: "22:00" / "07:00" on a
 * 24-hour clock, "10:00 PM" / "7:00 AM" on a 12-hour one.
 */
export function formatClockTime(hour: number, minute = 0, options: Pick<DateTextOptions, 'locale'> = {}): string {
  const date = new Date(Date.UTC(2000, 0, 1, hour, minute));
  return formatDateText(date, 'time', { locale: options.locale, timeZone: 'UTC' });
}

/**
 * A stored "HH:MM" wall-clock time (24-hour, as the door and office editors
 * save it) in the reader's notation. Returns the input unchanged if it is not
 * a time.
 */
export function formatHhMm(value: string, options: Pick<DateTextOptions, 'locale'> = {}): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return value;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (hour > 23 || minute > 59) return value;
  return formatClockTime(hour, minute, options);
}

/** Whether Intl really has data for `tag` (rather than falling back). */
function resolvesTo(tag: string): boolean {
  try {
    return new Intl.DateTimeFormat(tag).resolvedOptions().locale === tag;
  } catch {
    return false;
  }
}

/**
 * The locale to write dates in for someone whose preferred languages are
 * `tags` (a browser's navigator.languages, most preferred first). The app is
 * English-only, so this is ALWAYS an English locale, carrying the conventions
 * of the reader's region:
 *   - an English tag stays as the reader set it (en-GB, en-US);
 *   - otherwise the English variant for their region: nl-NL → en-NL
 *     ("12 Mar 2026, 15:05"), ms-MY → en-MY, de-CH → en-CH;
 *   - where Intl has no English variant for the region (zh-TW, ko-KR, pt-BR),
 *     still English: `en-<region>`, whose dates follow plain English order
 *     ("Mar 12, 2026") while the region subtag keeps that region's first day of
 *     the week, and whose clock is the reader's own — an hour-cycle keyword is
 *     added where English's default differs (pt-BR → en-BR-u-hc-h23, "Mar 12,
 *     2026, 15:05"; zh-TW → en-TW, whose 12-hour clock already matches).
 * Plain `en` when no tag is usable. Idempotent — an English result maps to
 * itself — which is what lets the server re-check a locale the app recorded
 * (pickRecipientTextFormat) and get the same notation the screen used.
 */
export function englishRegionalLocale(tags: readonly string[]): string {
  for (const tag of tags) {
    let locale: Intl.Locale;
    try {
      locale = new Intl.Locale(tag);
    } catch {
      continue;
    }
    // Already English: keep it, with any clock the tag asked for.
    if (locale.language === 'en') return withHourCycle(locale.baseName, locale.hourCycle);
    const region = locale.region ?? locale.maximize().region;
    if (!region) return 'en';
    const english = `en-${region}`;
    if (resolvesTo(english)) return english;
    // No English data for this region: English words and date order, the
    // region kept for its week, and the clock the reader's own locale uses.
    const is24 = uses24HourClock(locale.baseName);
    return is24 === uses24HourClock(english) ? english : withHourCycle(english, is24 ? 'h23' : 'h12');
  }
  return 'en';
}

/** `tag` with a Unicode hour-cycle keyword, when there is one to add. */
function withHourCycle(tag: string, hourCycle: string | undefined): string {
  return hourCycle ? `${tag}-u-hc-${hourCycle}` : tag;
}

/**
 * Territories whose weeks start on Sunday (CLDR 48 weekData), for runtimes that
 * cannot answer Intl.Locale's week-info question themselves. Everywhere else
 * the week starts on Monday.
 */
const SUNDAY_FIRST = new Set(
  (
    'AG AS BD BR BS BT BW BZ CA CO DM DO ET GT GU HK HN ID IL IN IS JM JP KE KH KR LA MH MM MO ' +
    'MT MX MZ NI NP PA PE PH PK PR PT PY SA SG SV TH TT TW UM US VE VI WS YE ZA ZW'
  ).split(' '),
);

/** The first day of the week in `locale`: 1 = Monday … 7 = Sunday. */
export function firstDayOfWeek(locale?: string): number {
  try {
    const intlLocale = new Intl.Locale(locale ?? new Intl.DateTimeFormat().resolvedOptions().locale);
    // getWeekInfo() is the current spelling; older engines expose weekInfo.
    const withWeek = intlLocale as Intl.Locale & {
      getWeekInfo?: () => { firstDay: number };
      weekInfo?: { firstDay: number };
    };
    const info = withWeek.getWeekInfo?.() ?? withWeek.weekInfo;
    if (info?.firstDay) return info.firstDay;
    const region = intlLocale.region ?? intlLocale.maximize().region;
    return region && SUNDAY_FIRST.has(region) ? 7 : 1;
  } catch {
    return 1;
  }
}

/**
 * The weekday names in calendar order for `locale`, starting on its first day
 * of the week — ["M", "T", …] or ["S", "M", …] for 'narrow'.
 */
export function weekdayNames(
  width: 'narrow' | 'short' | 'long',
  locale?: string,
  firstDay: number = firstDayOfWeek(locale),
): string[] {
  const format = new Intl.DateTimeFormat(locale, { weekday: width, timeZone: 'UTC' });
  // 1 January 2024 was a Monday, so day n (1 = Monday) is 2024-01-n.
  return Array.from({ length: 7 }, (_, i) => {
    const isoDay = ((firstDay - 1 + i) % 7) + 1;
    return format.format(new Date(Date.UTC(2024, 0, isoDay)));
  });
}

/**
 * The locale and time zone to write a server-sent message in, for its
 * recipient. `settings` is their users/{uid}/settings/notifications document
 * (self-written, so untrusted): `locale` and `timeZone` are what their app
 * recorded on its last launch. The locale is passed through
 * englishRegionalLocale again, so whatever the document holds, the message is
 * in English, in the notation the recipient's screen uses. The time zone falls
 * back to the one saved with their quiet hours, then their site's, then UTC. A
 * recipient the app has never seen gets en-US, the company's home notation.
 */
export function pickRecipientTextFormat(
  settings: unknown,
  siteTimeZone?: string,
): { locale: string; timeZone: string } {
  const s = (settings !== null && typeof settings === 'object' ? settings : {}) as Record<string, unknown>;
  const quiet = (s.quietHours !== null && typeof s.quietHours === 'object' ? s.quietHours : {}) as Record<
    string,
    unknown
  >;
  const locale =
    typeof s.locale === 'string' && s.locale.length <= 35 && isLocaleTag(s.locale)
      ? englishRegionalLocale([s.locale])
      : 'en-US';
  const zones = [s.timeZone, quiet.timeZone, siteTimeZone];
  const timeZone = zones.find((z): z is string => typeof z === 'string' && isZone(z)) ?? 'UTC';
  return { locale, timeZone };
}

/** Whether `tag` is a well-formed BCP 47 language tag. */
function isLocaleTag(tag: string): boolean {
  try {
    new Intl.Locale(tag);
    return true;
  } catch {
    return false;
  }
}

/** Whether `zone` is an IANA time zone this runtime can evaluate. */
function isZone(zone: string): boolean {
  if (zone.length === 0 || zone.length > 64) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}
